import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { fetchUsers, usersKey } from './userQueries'

type Injected = {
  /** Nazwa propsa, pod którą komponent oczekuje listy. */
  listProp: 'users' | 'availableUsers'
  /** Nazwa propsa, pod którą komponent oczekuje funkcji pobierającej. */
  fetchProp: 'getUsers' | 'getAvailableUsers'
}

/**
 * Wstrzykuje pełną listę użytkowników z react-query do kontenera zakładki.
 *
 * Slice `users.users` czytały cztery miejsca pod dwiema różnymi nazwami propsów
 * (`users` w Raportach i Firmach, `availableUsers` w edycji firmy), więc HOC przyjmuje
 * nazwy zamiast je narzucać.
 *
 * Przepuszcza ref: panel administracyjny sięga po instancję zakładki.
 */
export function withUsers<P extends object>(
  Wrapped: React.ComponentType<P>,
  { listProp, fetchProp }: Injected,
) {
  const WithUsers = React.forwardRef<unknown, any>((props, ref) => {
    const queryClient = useQueryClient()
    const companyId = (props as any).currentCompanyId ?? null

    const { data: users = [] } = useQuery({
      queryKey: usersKey(companyId),
      queryFn: () => fetchUsers(companyId ?? undefined),
    })

    // Komponenty czekają na wynik pobrania, więc musi zwracać listę.
    const fetchList = React.useCallback(
      (id?: string, tenantId?: string) =>
        queryClient.fetchQuery({
          queryKey: usersKey(id ?? null),
          queryFn: () => fetchUsers(id, tenantId),
        }),
      [queryClient],
    )

    const injected = {
      [listProp]: users,
      [fetchProp]: fetchList,
    }

    return <Wrapped ref={ref as any} {...(props as P)} {...(injected as any)} />
  })

  WithUsers.displayName = `withUsers(${
    (Wrapped as any).displayName || (Wrapped as any).name || 'Component'
  })`

  return WithUsers
}
